import axios from 'axios';
import { API_CONFIG } from '../config/env';
import { asArray } from '../utils/apiResponse';
import { getAccessToken, getCurrentUserId, getCurrentUserRole } from '../utils/auth';

export interface GlobalStats {
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
}

export interface UserStats {
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
}

export interface SubUser {
  userId: number;
  stats: UserStats;
}

export interface UserStatsResponse {
  userId: number;
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
  subUsers: SubUser[];
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  usdtWallet: string;
  role: 'Admin' | 'SuperAgent' | 'Agent';
  createdById?: number;
}

const extractWalletAddress = (user: any): string => {
  const candidates = [
    user?.usdcWallet,
    user?.usdtWallet,
    user?.walletAddress,
    user?.wallet,
    user?.address,
  ];
  const wallet = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return typeof wallet === 'string' ? wallet : '';
};

export interface Transaction {
  id: number;
  userId: number;
  userName: string;
  amount: number;
  currency: string;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed';
  timestamp: string;
  createdAt?: string;
  updatedAt?: string;
  paymentMethod?: string;
  fiatAmount?: number;
  fiatCurrency?: string;
}

const USERS_CACHE_TTL_MS = 30_000;

const unwrapPayload = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
};

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeStats = (raw: unknown): GlobalStats => {
  const o = (unwrapPayload(raw) ?? {}) as Record<string, unknown>;
  return {
    totalIssued: toNumber(o.totalIssued ?? o.TotalIssued),
    totalCompleted: toNumber(o.totalCompleted ?? o.TotalCompleted),
    totalPending: toNumber(o.totalPending ?? o.TotalPending),
    totalFailed: toNumber(o.totalFailed ?? o.TotalFailed),
    totalVolume: toNumber(o.totalVolume ?? o.TotalVolume),
  };
};

const normalizeUserStats = (raw: unknown): UserStatsResponse => {
  const o = (unwrapPayload(raw) ?? {}) as Record<string, unknown>;
  const subUsersRaw = asArray<Record<string, unknown>>(o.subUsers ?? o.SubUsers);
  const stats = normalizeStats(o);
  return {
    userId: toNumber(o.userId ?? o.UserId),
    ...stats,
    subUsers: subUsersRaw.map((su) => ({
      userId: toNumber(su.userId ?? su.UserId),
      stats: normalizeStats(su.stats ?? su.Stats),
    })),
  };
};

const normalizeRole = (role: unknown): User['role'] => {
  if (role === 'Admin' || role === 'SuperAgent' || role === 'Agent') return role;
  if (role === 0 || role === '0') return 'Admin';
  if (role === 1 || role === '1') return 'SuperAgent';
  if (role === 2 || role === '2') return 'Agent';
  return 'Agent';
};

const normalizeUser = (raw: Record<string, unknown>): User => ({
  id: toNumber(raw.id ?? raw.Id),
  name: String(raw.name ?? raw.Name ?? ''),
  email: String(raw.email ?? raw.Email ?? ''),
  phone: String(raw.phone ?? raw.Phone ?? ''),
  usdtWallet: extractWalletAddress(raw),
  role: normalizeRole(raw.role ?? raw.Role),
  createdById:
    raw.createdById != null || raw.CreatedById != null
      ? toNumber(raw.createdById ?? raw.CreatedById)
      : undefined,
});

const parseUsersFromResponse = (payload: unknown): User[] => {
  const unwrapped = unwrapPayload(payload);
  const rawItems: Record<string, unknown>[] = [];

  if (Array.isArray(unwrapped)) {
    unwrapped.forEach((item) => {
      if (item && typeof item === 'object') rawItems.push(item as Record<string, unknown>);
    });
  } else if (unwrapped && typeof unwrapped === 'object') {
    rawItems.push(unwrapped as Record<string, unknown>);
  } else {
    asArray<Record<string, unknown>>(payload).forEach((item) => rawItems.push(item));
  }

  return rawItems.map(normalizeUser);
};

const normalizeTransaction = (raw: Record<string, unknown>): Transaction => ({
  id: toNumber(raw.id ?? raw.Id),
  userId: toNumber(raw.userId ?? raw.UserId),
  userName: String(raw.userName ?? raw.UserName ?? ''),
  amount: toNumber(raw.cryptoAmount ?? raw.CryptoAmount ?? raw.amount ?? raw.Amount),
  currency: String(raw.cryptoCurrency ?? raw.CryptoCurrency ?? raw.currency ?? raw.Currency ?? ''),
  status: String(raw.status ?? raw.Status ?? 'Pending') as Transaction['status'],
  timestamp: String(raw.timestamp ?? raw.Timestamp ?? ''),
  fiatAmount: raw.fiatAmount != null || raw.FiatAmount != null
    ? toNumber(raw.fiatAmount ?? raw.FiatAmount)
    : undefined,
  fiatCurrency: raw.fiatCurrency != null || raw.FiatCurrency != null
    ? String(raw.fiatCurrency ?? raw.FiatCurrency)
    : undefined,
});

class DashboardService {
  private usersCache: { data: User[]; at: number; key: string } | null = null;
  private usersInflight: Promise<User[]> | null = null;

  private usersCacheKey(): string {
    return `${getCurrentUserId() ?? ''}:${getCurrentUserRole() ?? ''}`;
  }

  private getAuthHeaders() {
    const token = getAccessToken();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
  }

  /** Clear after user create/update so lists stay fresh. */
  invalidateUsersCache(): void {
    this.usersCache = null;
  }

  async getGlobalStats(): Promise<GlobalStats> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Stats/global`, {
      headers: this.getAuthHeaders(),
    });
    return normalizeStats(response.data);
  }

  async getUserStats(userId: number): Promise<UserStatsResponse> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Stats/user/${userId}`, {
      headers: this.getAuthHeaders(),
    });
    return normalizeUserStats(response.data);
  }

  async getUsers(options?: { force?: boolean }): Promise<User[]> {
    const force = options?.force === true;
    const cacheKey = this.usersCacheKey();
    const now = Date.now();
    if (
      !force &&
      this.usersCache &&
      this.usersCache.key === cacheKey &&
      this.usersCache.data.length > 0 &&
      now - this.usersCache.at < USERS_CACHE_TTL_MS
    ) {
      return this.usersCache.data;
    }
    if (this.usersInflight) return this.usersInflight;

    this.usersInflight = axios
      .get(`${API_CONFIG.BASE_URL}/Users`, { headers: this.getAuthHeaders() })
      .then((response) => {
        const data = parseUsersFromResponse(response.data);
        if (data.length > 0) {
          this.usersCache = { data, at: Date.now(), key: cacheKey };
        }
        return data;
      })
      .finally(() => {
        this.usersInflight = null;
      });

    return this.usersInflight;
  }

  async getTransactions(limit: number = 5): Promise<Transaction[]> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Transactions`, {
      headers: this.getAuthHeaders(),
      params: { page: 1, pageSize: limit },
    });
    return asArray<Record<string, unknown>>(response.data).map(normalizeTransaction);
  }
}

export const dashboardService = new DashboardService();
